import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  RadioInputDiv,
  RadioWrapper,
  CheckboxWrapper,
  ActionButtonsRow,
  Result,
  MathDiv,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  AlertDiv,
  AdvancedOptionsButton,
  ImageDiv,
  SvgImage,
  CopyImageButton,
  ErrorMessage,
} from '../../../styles/global'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { FaRegCopy } from 'react-icons/fa6'
import { FaPlusCircle, FaMinusCircle } from 'react-icons/fa'
import { MdError } from 'react-icons/md'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Customized,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  usePlotArea,
  useXAxisDomain,
  useYAxisDomain,
} from 'recharts'
import { Tooltip as ReactTooltip } from 'react-tooltip'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
import { roundToDecimals } from '../../../utils/numberFormat'
import { TableContainer, AddButton, SectionTitle, ChartLegend, Table, Th, Td, Tr } from './styles'

const DIAGRAM_SIZE = 350
const DIAGRAM_MARGIN = 50
const FLAG_HATCH_COUNT = 10

const CONCRETE_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'

function barAreaCm2(diameterMm) {
  const diameterCm = diameterMm / 10
  return (Math.PI * diameterCm * diameterCm) / 4
}

// Parábola-retângulo com os parâmetros default do back-end, usados para desenhar o diagrama de
// tensões solicitante (a calculadora não expõe esses coeficientes para o usuário sobrescrever
// no desenho, apenas no cálculo).
function concreteStress(strain, fcd, epsC2 = 0.002, n = 2) {
  if (strain <= 0) return 0
  if (strain >= epsC2) return fcd
  return fcd * (1 - Math.pow(1 - strain / epsC2, n))
}

const SECOND_ORDER_METHOD_LABELS = {
  none: 'Dispensada',
  'approximate-curvature': 'Pilar-padrão (curvatura aproximada)',
  'NM-curvature': 'Diagrama N-M-1/r',
  'general-NM-curvature-uniaxial': 'Método geral (N-M-1/r por direção)',
}

function secondOrderMethodLabel(method) {
  return SECOND_ORDER_METHOD_LABELS[method] || method || '-'
}

const POSITION_LABELS = {
  top: 'Topo',
  intermediate: 'Intermediária',
  base: 'Base',
}

function positionLabel(position) {
  if (POSITION_LABELS[position]) return POSITION_LABELS[position]
  const match = /^node-(\d+)$/.exec(position || '')
  if (match) return `Seção ${match[1]}`
  return position || '-'
}

function warningMessages(warningsDetailed, warnings) {
  if (warningsDetailed && warningsDetailed.length > 0) {
    return warningsDetailed.map((warning) => warning.message)
  }
  return warnings || []
}

const POSITION_COLORS = {
  top: '#f59e0b',
  intermediate: '#16a34a',
  base: '#8b5cf6',
}

function hexToRgb(hex) {
  const value = hex.replace('#', '')
  const bigint = parseInt(value, 16)
  return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 }
}

function interpolateColor(colorA, colorB, t) {
  const a = hexToRgb(colorA)
  const b = hexToRgb(colorB)
  const r = Math.round(a.r + (b.r - a.r) * t)
  const g = Math.round(a.g + (b.g - a.g) * t)
  const bl = Math.round(a.b + (b.b - a.b) * t)
  return `rgb(${r}, ${g}, ${bl})`
}

// Metodo geral retorna mais de 3 posicoes (um no por segmento); as extras (node-N) recebem uma
// cor interpolada entre a base e o topo, de acordo com a altura relativa no lance.
function positionColor(position) {
  if (POSITION_COLORS[position.key]) return POSITION_COLORS[position.key]
  const relativeZ = typeof position.relativeZ === 'number' ? position.relativeZ : 0.5
  return interpolateColor(POSITION_COLORS.base, POSITION_COLORS.top, relativeZ)
}

function envelopePontos(data) {
  if (!data.minimumMomentEnvelope?.points) return []

  const checkMap = new Map(
    data.allCases?.filter((c) => c.type === 'minimum-envelope').map((c) => [c.tag, c.sectionCheck]) || []
  )

  const pontos = data.minimumMomentEnvelope.points.map((p) => ({
    key: p.key,
    mx: p.mxSdKnm,
    my: p.mySdKnm,
    ratio: checkMap.get(p.key)?.ratio,
    ok: checkMap.get(p.key)?.ok,
  }))

  // Os pontos vem indexados pelo tag (minimum-envelope-N), cuja ordem NAO acompanha o perimetro da elipse.
  // Ordenamos pelo angulo polar real (atan2) em torno do centroide para tracar o contorno em sequencia,
  // evitando o poligono auto-intersectado (efeito "estrela").
  const cx = pontos.reduce((sum, p) => sum + p.mx, 0) / (pontos.length || 1)
  const cy = pontos.reduce((sum, p) => sum + p.my, 0) / (pontos.length || 1)
  return pontos.sort((a, b) => Math.atan2(a.my - cy, a.mx - cx) - Math.atan2(b.my - cy, b.mx - cx))
}

// Poligono SVG fechado da envoltoria minima, desenhado dentro do grafico N-Mx-My via <Customized>.
// No Recharts v3 os scales dos eixos nao sao mais passados por props (xAxisMap/yAxisMap); reconstruimos
// o mapeamento linear a partir da area do plot (px) e dos dominios calculados dos eixos.
function MinimumEnvelopePolygon({ points }) {
  const plotArea = usePlotArea()
  const xDomain = useXAxisDomain(0)
  const yDomain = useYAxisDomain(0)
  if (!points || points.length === 0 || !plotArea || !Array.isArray(xDomain) || !Array.isArray(yDomain)) {
    return null
  }
  const [xMin, xMax] = xDomain
  const [yMin, yMax] = yDomain
  const xRange = xMax - xMin || 1
  const yRange = yMax - yMin || 1
  const toX = (v) => plotArea.x + ((v - xMin) / xRange) * plotArea.width
  const toY = (v) => plotArea.y + ((yMax - v) / yRange) * plotArea.height
  const polygon = points.map((p) => `${toX(p.mx)},${toY(p.my)}`).join(' ')
  return <polygon points={polygon} fill="#e5e7eb" fillOpacity={0.45} stroke="#d1d5db" strokeWidth={1} />
}

function buildPositionTable(processed) {
  const rows = []

  const real = processed.allCases?.find((c) => c.tag === 'real')
  if (real?.positionChecks) {
    real.positionChecks
      .slice()
      .sort((a, b) => b.relativeZ - a.relativeZ)
      .forEach((p) => {
        rows.push({
          key: p.key,
          label: p.label,
          z: p.z,
          nSd: p.nSd,
          mxSdKnm: p.mxSdKnm,
          mySdKnm: p.mySdKnm,
          fs: p.fs,
          ratio: p.ratio,
          ok: p.ok,
        })
      })
  }

  const crit = processed.allCases?.find((c) => c.tag === processed.governingCase)
  if (crit?.type === 'minimum-envelope') {
    rows.push({
      key: 'minimum-envelope',
      label: 'Envoltória mínima (crítica)',
      z: null,
      nSd: crit.finalEfforts?.nSd,
      mxSdKnm: crit.minimumEnvelope?.mxTotMinKnm,
      mySdKnm: crit.minimumEnvelope?.myTotMinKnm,
      fs: crit.sectionCheck?.fs,
      ratio: crit.sectionCheck?.ratio,
      ok: crit.sectionCheck?.ok,
    })
  }

  return rows
}

function processResponseData(data) {
  const processed = { ...data }
  processed.positionChecks = buildPositionTable(processed)
  return processed
}

export function ConcreteColumnDesign() {
  const { canCalculate, updateFromHeaders, handleRateLimitError } = useRateLimit()

  // Materiais
  const [fck, setFck] = useState(30)
  const [fyk, setFyk] = useState(500)
  const [gammaC, setGammaC] = useState(1.4)
  const [gammaS, setGammaS] = useState(1.15)
  const [showAdvancedMaterials, setShowAdvancedMaterials] = useState(false)
  const [alphaCc, setAlphaCc] = useState(0.85)
  const [etaC, setEtaC] = useState(1)
  const [epsC2, setEpsC2] = useState(0.002)
  const [epsCu, setEpsCu] = useState(0.0035)
  const [concreteDiagramN, setConcreteDiagramN] = useState(2)
  const [Es, setEs] = useState(210000)

  // Geometria
  const [b, setB] = useState('')
  const [h, setH] = useState('')
  const [length, setLength] = useState('')

  // Barras
  const [barDiameter, setBarDiameter] = useState('')
  const [barCoverage, setBarCoverage] = useState('')
  const [barNx, setBarNx] = useState('')
  const [barNy, setBarNy] = useState('')
  const [bars, setBars] = useState([])
  const inputImageRef = useRef(null)
  const stressDiagramRef = useRef(null)

  // Esforços
  const [nSd, setNSd] = useState('')
  const [mxA, setMxA] = useState('')
  const [mxB, setMxB] = useState('')
  const [myA, setMyA] = useState('')
  const [myB, setMyB] = useState('')

  // Opções
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)
  const [useMinimumMoment, setUseMinimumMoment] = useState(true)
  const [useLocalSecondOrder, setUseLocalSecondOrder] = useState(true)
  const [alwaysCalculateLocalSecondOrder, setAlwaysCalculateLocalSecondOrder] = useState(true)
  const [localSecondOrderMethod, setLocalSecondOrderMethod] = useState('approximate-curvature')
  const [generateMinimumMomentCandidates, setGenerateMinimumMomentCandidates] = useState(true)
  const [useConservativeDoubleMinimum, setUseConservativeDoubleMinimum] = useState(false)
  const [limitPinnedEndMoments, setLimitPinnedEndMoments] = useState(true)
  const [maxSlendernessForApproximateCurvature, setMaxSlendernessForApproximateCurvature] = useState(90)
  const [generalMethodSegments, setGeneralMethodSegments] = useState(10)
  const [generalMethodMaxIterations, setGeneralMethodMaxIterations] = useState(80)
  const [generalMethodToleranceDisplacement, setGeneralMethodToleranceDisplacement] = useState(0.001)
  const [generalMethodToleranceRelativeDisplacement, setGeneralMethodToleranceRelativeDisplacement] = useState(0.0001)
  const [generalMethodRelaxationFactor, setGeneralMethodRelaxationFactor] = useState(1)

  // Identificação
  const [columnId, setColumnId] = useState('')

  // Resultados
  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isFormValid, setIsFormValid] = useState(false)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-column-design-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory))
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)

      for (let keep = newHistory.length; keep > 0; keep--) {
        try {
          localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory.slice(0, keep)))
          break
        } catch (error) {
          if (keep === 1) {
            console.error('Erro ao salvar histórico (excedeu a cota de armazenamento):', error)
            localStorage.removeItem(HISTORY_KEY)
          }
        }
      }

      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setGammaC(entry.gammaC || 1.4)
      setGammaS(entry.gammaS || 1.15)
      setAlphaCc(entry.alphaCc || 0.85)
      setEtaC(entry.etaC || 1)
      setEpsC2(entry.epsC2 || 0.002)
      setEpsCu(entry.epsCu || 0.0035)
      setConcreteDiagramN(entry.concreteDiagramN || 2)
      setEs(entry.Es || 210000)
      setB(entry.b)
      setH(entry.h)
      setLength(entry.length)
      setBarDiameter(entry.barDiameter)
      setBarCoverage(entry.barCoverage)
      setBarNx(entry.barNx)
      setBarNy(entry.barNy)
      setBars(entry.bars || [])
      setNSd(entry.nSd)
      setMxA(entry.mxA)
      setMxB(entry.mxB)
      setMyA(entry.myA)
      setMyB(entry.myB)
      setUseMinimumMoment(entry.useMinimumMoment !== false)
      setUseLocalSecondOrder(entry.useLocalSecondOrder !== false)
      setAlwaysCalculateLocalSecondOrder(entry.alwaysCalculateLocalSecondOrder !== false)
      setLocalSecondOrderMethod(entry.localSecondOrderMethod || 'approximate-curvature')
      setGenerateMinimumMomentCandidates(entry.generateMinimumMomentCandidates !== false)
      setUseConservativeDoubleMinimum(entry.useConservativeDoubleMinimum || false)
      setLimitPinnedEndMoments(entry.limitPinnedEndMoments !== false)
      setMaxSlendernessForApproximateCurvature(entry.maxSlendernessForApproximateCurvature || 90)
      setGeneralMethodSegments(entry.generalMethodSegments || 10)
      setGeneralMethodMaxIterations(entry.generalMethodMaxIterations || 80)
      setGeneralMethodToleranceDisplacement(entry.generalMethodToleranceDisplacement || 0.001)
      setGeneralMethodToleranceRelativeDisplacement(entry.generalMethodToleranceRelativeDisplacement || 0.0001)
      setGeneralMethodRelaxationFactor(entry.generalMethodRelaxationFactor || 1)
      setColumnId(entry.columnId)
      setResult(entry.result)
      setData(processResponseData(entry.data))
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (['barDiameter', 'barCoverage', 'barNx', 'barNy'].includes(name)) {
      return null
    }

    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (!['mxA', 'mxB', 'myA', 'myB'].includes(name) && numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.gammaC = validateField('gammaC', gammaC)
    newErrors.gammaS = validateField('gammaS', gammaS)
    newErrors.b = validateField('b', b)
    newErrors.h = validateField('h', h)
    newErrors.length = validateField('length', length)
    newErrors.nSd = validateField('nSd', nSd)
    newErrors.mxA = validateField('mxA', mxA)
    newErrors.mxB = validateField('mxB', mxB)
    newErrors.myA = validateField('myA', myA)
    newErrors.myB = validateField('myB', myB)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors && bars.length > 0)
  }, [fck, fyk, gammaC, gammaS, b, h, length, nSd, mxA, mxB, myA, myB, bars, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  function InputImage() {
    if (!b || !h) return null

    const padding = 20
    const scale = (DIAGRAM_SIZE - 2 * padding) / Math.max(Number(b), Number(h))
    const offsetX = padding + (DIAGRAM_SIZE - Number(b) * scale) / 2
    const offsetY = padding + (DIAGRAM_SIZE - Number(h) * scale) / 2

    // Coordenadas centradas: converter para pixel
    const toPixel = (coord, isY = false) => {
      if (isY) {
        return offsetY + (Number(h) / 2 + coord) * scale
      }
      return offsetX + (Number(b) / 2 + coord) * scale
    }

    // Seção retangular (canto inferior esquerdo em 0,0)
    const rectX = offsetX
    const rectY = offsetY
    const rectW = Number(b) * scale
    const rectH = Number(h) * scale

    return (
      <SvgImage
        ref={inputImageRef}
        viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={DIAGRAM_SIZE} height={DIAGRAM_SIZE} fill="white" />
        <rect x={rectX} y={rectY} width={rectW} height={rectH} fill="#e5e7eb" stroke="#1f2937" strokeWidth="2" />

        {/* Eixos de coordenadas (centrados) */}
        <line
          x1={toPixel(-Number(b) / 2 - 5)}
          y1={toPixel(0, true)}
          x2={toPixel(Number(b) / 2 + 5)}
          y2={toPixel(0, true)}
          stroke="#9ca3af"
          strokeWidth="0.8"
          strokeDasharray="4 2"
        />
        <line
          x1={toPixel(0)}
          y1={toPixel(-Number(h) / 2 - 5, true)}
          x2={toPixel(0)}
          y2={toPixel(Number(h) / 2 + 5, true)}
          stroke="#9ca3af"
          strokeWidth="0.8"
          strokeDasharray="4 2"
        />

        {/* Barras */}
        {bars.map((bar, index) => {
          const px = toPixel(bar.x)
          const py = toPixel(bar.y, true)
          const barRadius = Math.max(3, barAreaCm2(bar.diameterMm) * scale * 0.15)
          return <circle key={index} cx={px} cy={py} r={barRadius} fill="#1d4ed8" stroke="#1e40af" strokeWidth="0.5" />
        })}

        {/* Dimensões */}
        <text
          x={rectX + rectW / 2}
          y={DIAGRAM_SIZE - 10}
          textAnchor="middle"
          fontSize="11"
          fill="#374151"
          fontFamily="Poppins"
        >
          b = {b} cm
        </text>
        <text x={10} y={rectY + rectH / 2 + 4} fontSize="11" fill="#374151" fontFamily="Poppins">
          h = {h} cm
        </text>
      </SvgImage>
    )
  }

  function StressDiagram({ stressDiagram }) {
    const {
      section,
      bars: stressBars,
      neutralAxis,
      compressedZone,
      strains,
      maxConcreteCompressionStress,
    } = stressDiagram

    const fcd = maxConcreteCompressionStress.value
    const maxDim = Math.max(section.b, section.h)
    const hasFlag = Boolean(neutralAxis.line) && compressedZone.polygon.length > 0

    // A seção é mantida ortogonal (sem rotação). A linha neutra é desenhada inclinada e o
    // diagrama de tensões fica apoiado sobre a face comprimida da seção, com as ordenadas de
    // tensão saindo perpendicularmente a essa face (parábola-retângulo), como no software de
    // referência.
    let flag = null
    if (hasFlag) {
      const magNormal = Math.hypot(neutralAxis.normal.x, neutralAxis.normal.y) || 1
      const nx = neutralAxis.normal.x / magNormal
      const ny = neutralAxis.normal.y / magNormal

      const p1 = neutralAxis.line.p1
      const center = { x: section.b / 2, y: section.h / 2 }

      const extreme = section.polygon.reduce(
        (best, point) => {
          const proj = point.x * nx + point.y * ny
          return proj > best.proj ? { point, proj } : best
        },
        { point: section.polygon[0], proj: section.polygon[0].x * nx + section.polygon[0].y * ny }
      )
      const extremePoint = extreme.point
      const depth = (extremePoint.x - p1.x) * nx + (extremePoint.y - p1.y) * ny

      const eps = 0.5
      const crossingOnEdge = [neutralAxis.line.p1, neutralAxis.line.p2].find(
        (crossing) => Math.abs(crossing.x - extremePoint.x) < eps || Math.abs(crossing.y - extremePoint.y) < eps
      )

      if (depth > 0.01 && crossingOnEdge) {
        const edgeVecX = extremePoint.x - crossingOnEdge.x
        const edgeVecY = extremePoint.y - crossingOnEdge.y
        const midX = (crossingOnEdge.x + extremePoint.x) / 2
        const midY = (crossingOnEdge.y + extremePoint.y) / 2
        let outX = -ny
        let outY = nx
        if ((midX - center.x) * outX + (midY - center.y) * outY < 0) {
          outX = -outX
          outY = -outY
        }

        const flagLen = 0.42 * maxDim
        const baseline = []
        const profile = []
        for (let i = 0; i <= FLAG_HATCH_COUNT * 2; i++) {
          const s = i / (FLAG_HATCH_COUNT * 2)
          const bx = crossingOnEdge.x + s * edgeVecX
          const by = crossingOnEdge.y + s * edgeVecY
          const dist = (bx - p1.x) * nx + (by - p1.y) * ny
          const strain = (strains.compressedExtreme * dist) / depth
          const stress = concreteStress(strain, fcd)
          baseline.push({ x: bx, y: by })
          profile.push({ x: bx + (stress / fcd) * flagLen * outX, y: by + (stress / fcd) * flagLen * outY })
        }

        const naDirX = neutralAxis.line.p2.x - neutralAxis.line.p1.x
        const naDirY = neutralAxis.line.p2.y - neutralAxis.line.p1.y
        const naDirLen = Math.hypot(naDirX, naDirY) || 1
        const secondLine = {
          p1: {
            x: extremePoint.x - (0.45 * maxDim * naDirX) / naDirLen,
            y: extremePoint.y - (0.45 * maxDim * naDirY) / naDirLen,
          },
          p2: {
            x: extremePoint.x + (0.25 * maxDim * naDirX) / naDirLen,
            y: extremePoint.y + (0.25 * maxDim * naDirY) / naDirLen,
          },
        }

        flag = { baseline, profile, tip: profile[profile.length - 1], secondLine }
      }
    }

    const naExtended = hasFlag
      ? (() => {
          const p1 = neutralAxis.line.p1
          const p2 = neutralAxis.line.p2
          const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
          const dx = (p2.x - p1.x) / len
          const dy = (p2.y - p1.y) / len
          const ext = 0.2 * maxDim
          return {
            p1: { x: p1.x - ext * dx, y: p1.y - ext * dy },
            p2: { x: p2.x + ext * dx, y: p2.y + ext * dy },
          }
        })()
      : null

    const allPoints = [...section.polygon]
    if (flag) {
      allPoints.push(...flag.baseline, ...flag.profile, flag.secondLine.p1, flag.secondLine.p2)
    }
    if (naExtended) {
      allPoints.push(naExtended.p1, naExtended.p2)
    }
    const xs = allPoints.map((point) => point.x)
    const ys = allPoints.map((point) => point.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const pad = 0.08 * maxDim
    const contentW = maxX - minX + 2 * pad
    const contentH = maxY - minY + 2 * pad
    const available = DIAGRAM_SIZE - DIAGRAM_MARGIN * 2
    const scale = contentW > 0 && contentH > 0 ? Math.min(available / contentW, available / contentH) : 1
    const offsetX = (DIAGRAM_SIZE - (maxX - minX) * scale) / 2 - minX * scale
    const offsetY = (DIAGRAM_SIZE - (maxY - minY) * scale) / 2 - minY * scale
    const px = (x) => offsetX + x * scale
    const py = (y) => offsetY + y * scale

    const toPoints = (points) => points.map((point) => `${px(point.x)},${py(point.y)}`).join(' ')

    return (
      <SvgImage
        ref={stressDiagramRef}
        viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width={DIAGRAM_SIZE} height={DIAGRAM_SIZE} fill="white" />
        <polygon points={toPoints(section.polygon)} fill="#d1d5db" stroke="black" strokeWidth="2" />
        {naExtended && (
          <line
            x1={px(naExtended.p1.x)}
            y1={py(naExtended.p1.y)}
            x2={px(naExtended.p2.x)}
            y2={py(naExtended.p2.y)}
            stroke="#374151"
            strokeWidth="1.5"
            strokeDasharray="7 5"
          />
        )}
        {flag && (
          <>
            <line
              x1={px(flag.secondLine.p1.x)}
              y1={py(flag.secondLine.p1.y)}
              x2={px(flag.secondLine.p2.x)}
              y2={py(flag.secondLine.p2.y)}
              stroke="#374151"
              strokeWidth="1.5"
              strokeDasharray="7 5"
            />
            <polygon
              points={`${toPoints(flag.baseline)} ${toPoints([...flag.profile].reverse())}`}
              fill="#93c5fd"
              fillOpacity="0.6"
              stroke="#1d4ed8"
              strokeWidth="1.5"
            />
            {flag.baseline.map(
              (basePoint, index) =>
                index % 2 === 0 &&
                index > 0 && (
                  <line
                    key={index}
                    x1={px(basePoint.x)}
                    y1={py(basePoint.y)}
                    x2={px(flag.profile[index].x)}
                    y2={py(flag.profile[index].y)}
                    stroke="#1d4ed8"
                    strokeWidth="0.8"
                  />
                )
            )}
            <line
              x1={px(flag.tip.x)}
              y1={py(flag.tip.y)}
              x2={px(flag.tip.x) + 12}
              y2={py(flag.tip.y) - 14}
              stroke="#1d4ed8"
              strokeWidth="1"
            />
            <text x={px(flag.tip.x) + 14} y={py(flag.tip.y) - 12} fontFamily="Poppins" fontSize="13" fill="#1d4ed8">
              fcd = {fcd} MPa
            </text>
          </>
        )}
        {stressBars.map((bar, index) => (
          <circle key={index} cx={px(bar.x)} cy={py(bar.y)} r={5} fill={bar.stress >= 0 ? '#1d4ed8' : '#f97316'} />
        ))}
      </SvgImage>
    )
  }

  function StrainDiagram({ stressDiagram }) {
    const { section, bars: strainBars, neutralAxis, strains } = stressDiagram
    const maxDim = Math.max(section.b, section.h)
    const hasFlag = Boolean(neutralAxis.line)

    let compressionFlag = null
    let tensionFlag = null
    if (hasFlag) {
      const magNormal = Math.hypot(neutralAxis.normal.x, neutralAxis.normal.y) || 1
      const nx = neutralAxis.normal.x / magNormal
      const ny = neutralAxis.normal.y / magNormal

      const p1 = neutralAxis.line.p1
      const center = { x: section.b / 2, y: section.h / 2 }

      const extremeComp = section.polygon.reduce(
        (best, point) => {
          const proj = point.x * nx + point.y * ny
          return proj > best.proj ? { point, proj } : best
        },
        { point: section.polygon[0], proj: section.polygon[0].x * nx + section.polygon[0].y * ny }
      )
      const extremeCompPoint = extremeComp.point
      const depthComp = (extremeCompPoint.x - p1.x) * nx + (extremeCompPoint.y - p1.y) * ny

      const extremeTrac = section.polygon.reduce(
        (best, point) => {
          const proj = point.x * nx + point.y * ny
          return proj < best.proj ? { point, proj } : best
        },
        { point: section.polygon[0], proj: section.polygon[0].x * nx + section.polygon[0].y * ny }
      )
      const extremeTracPoint = extremeTrac.point
      const depthTrac = (extremeTracPoint.x - p1.x) * nx + (extremeTracPoint.y - p1.y) * ny

      const eps = 0.5
      const crossingOnEdgeComp = [neutralAxis.line.p1, neutralAxis.line.p2].find(
        (crossing) => Math.abs(crossing.x - extremeCompPoint.x) < eps || Math.abs(crossing.y - extremeCompPoint.y) < eps
      )
      const crossingOnEdgeTrac = [neutralAxis.line.p1, neutralAxis.line.p2].find(
        (crossing) => Math.abs(crossing.x - extremeTracPoint.x) < eps || Math.abs(crossing.y - extremeTracPoint.y) < eps
      )

      const maxStrain = Math.max(Math.abs(strains.compressedExtreme), Math.abs(strains.oppositeExtreme))
      const flagLen = 0.7 * maxDim

      if (depthComp > 0.01 && crossingOnEdgeComp) {
        const edgeVecX = extremeCompPoint.x - crossingOnEdgeComp.x
        const edgeVecY = extremeCompPoint.y - crossingOnEdgeComp.y
        const midX = (crossingOnEdgeComp.x + extremeCompPoint.x) / 2
        const midY = (crossingOnEdgeComp.y + extremeCompPoint.y) / 2
        let outX = -ny
        let outY = nx
        if ((midX - center.x) * outX + (midY - center.y) * outY < 0) {
          outX = -outX
          outY = -outY
        }

        const baseline = []
        const profile = []
        for (let i = 0; i <= FLAG_HATCH_COUNT * 2; i++) {
          const s = i / (FLAG_HATCH_COUNT * 2)
          const bx = crossingOnEdgeComp.x + s * edgeVecX
          const by = crossingOnEdgeComp.y + s * edgeVecY
          const dist = (bx - p1.x) * nx + (by - p1.y) * ny
          const strain = (strains.compressedExtreme * dist) / depthComp
          baseline.push({ x: bx, y: by })
          profile.push({ x: bx + (strain / maxStrain) * flagLen * outX, y: by + (strain / maxStrain) * flagLen * outY })
        }
        compressionFlag = { baseline, profile }
      }

      if (Math.abs(depthTrac) > 0.01 && crossingOnEdgeTrac) {
        const edgeVecX = extremeTracPoint.x - crossingOnEdgeTrac.x
        const edgeVecY = extremeTracPoint.y - crossingOnEdgeTrac.y
        const midX = (crossingOnEdgeTrac.x + extremeTracPoint.x) / 2
        const midY = (crossingOnEdgeTrac.y + extremeTracPoint.y) / 2
        let outX = -ny
        let outY = nx
        if ((midX - center.x) * outX + (midY - center.y) * outY > 0) {
          outX = -outX
          outY = -outY
        }

        const baseline = []
        const profile = []
        for (let i = 0; i <= FLAG_HATCH_COUNT * 2; i++) {
          const s = i / (FLAG_HATCH_COUNT * 2)
          const bx = crossingOnEdgeTrac.x + s * edgeVecX
          const by = crossingOnEdgeTrac.y + s * edgeVecY
          const dist = (bx - p1.x) * nx + (by - p1.y) * ny
          const strain = (strains.oppositeExtreme * dist) / depthTrac
          baseline.push({ x: bx, y: by })
          profile.push({ x: bx + (strain / maxStrain) * flagLen * outX, y: by + (strain / maxStrain) * flagLen * outY })
        }
        tensionFlag = { baseline, profile }
      }
    }

    const naExtended = hasFlag
      ? (() => {
          const p1 = neutralAxis.line.p1
          const p2 = neutralAxis.line.p2
          const len = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1
          const dx = (p2.x - p1.x) / len
          const dy = (p2.y - p1.y) / len
          const ext = 0.2 * maxDim
          return {
            p1: { x: p1.x - ext * dx, y: p1.y - ext * dy },
            p2: { x: p2.x + ext * dx, y: p2.y + ext * dy },
          }
        })()
      : null

    const allPoints = [...section.polygon]
    if (compressionFlag) {
      allPoints.push(...compressionFlag.baseline, ...compressionFlag.profile)
    }
    if (tensionFlag) {
      allPoints.push(...tensionFlag.baseline, ...tensionFlag.profile)
    }
    if (naExtended) {
      allPoints.push(naExtended.p1, naExtended.p2)
    }
    const xs = allPoints.map((point) => point.x)
    const ys = allPoints.map((point) => point.y)
    const minX = Math.min(...xs)
    const maxX = Math.max(...xs)
    const minY = Math.min(...ys)
    const maxY = Math.max(...ys)
    const pad = 0.08 * maxDim
    const contentW = maxX - minX + 2 * pad
    const contentH = maxY - minY + 2 * pad
    const available = DIAGRAM_SIZE - DIAGRAM_MARGIN * 2
    const scale = contentW > 0 && contentH > 0 ? Math.min(available / contentW, available / contentH) : 1
    const offsetX = (DIAGRAM_SIZE - (maxX - minX) * scale) / 2 - minX * scale
    const offsetY = (DIAGRAM_SIZE - (maxY - minY) * scale) / 2 - minY * scale
    const px = (x) => offsetX + x * scale
    const py = (y) => offsetY + y * scale

    const toPoints = (points) => points.map((point) => `${px(point.x)},${py(point.y)}`).join(' ')

    return (
      <SvgImage viewBox={`0 0 ${DIAGRAM_SIZE} ${DIAGRAM_SIZE}`} fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width={DIAGRAM_SIZE} height={DIAGRAM_SIZE} fill="white" />
        <polygon points={toPoints(section.polygon)} fill="#d1d5db" stroke="black" strokeWidth="2" />
        {naExtended && (
          <line
            x1={px(naExtended.p1.x)}
            y1={py(naExtended.p1.y)}
            x2={px(naExtended.p2.x)}
            y2={py(naExtended.p2.y)}
            stroke="#374151"
            strokeWidth="1.5"
            strokeDasharray="7 5"
          />
        )}
        {compressionFlag && (
          <>
            <line
              x1={px(compressionFlag.baseline[0].x)}
              y1={py(compressionFlag.baseline[0].y)}
              x2={px(compressionFlag.baseline[compressionFlag.baseline.length - 1].x)}
              y2={py(compressionFlag.baseline[compressionFlag.baseline.length - 1].y)}
              stroke="#6366f1"
              strokeWidth="2"
            />
            <polygon
              points={`${toPoints(compressionFlag.baseline)} ${toPoints([...compressionFlag.profile].reverse())}`}
              fill="#c7d2fe"
              fillOpacity="0.6"
              stroke="#6366f1"
              strokeWidth="1.5"
            />
            {compressionFlag.baseline.map(
              (basePoint, index) =>
                index % 2 === 0 &&
                index > 0 && (
                  <line
                    key={`comp-${index}`}
                    x1={px(basePoint.x)}
                    y1={py(basePoint.y)}
                    x2={px(compressionFlag.profile[index].x)}
                    y2={py(compressionFlag.profile[index].y)}
                    stroke="#6366f1"
                    strokeWidth="0.8"
                  />
                )
            )}
            <text
              x={px(compressionFlag.profile[compressionFlag.profile.length - 1].x) - 25}
              y={py(compressionFlag.profile[compressionFlag.profile.length - 1].y) - 12}
              fontFamily="sans-serif"
              fontSize="13"
              fill="#6366f1"
              fontWeight="bold"
              textAnchor="end"
            >
              {(strains.compressedExtreme * 1000).toFixed(2)}‰
            </text>
          </>
        )}
        {tensionFlag && (
          <>
            <line
              x1={px(tensionFlag.baseline[0].x)}
              y1={py(tensionFlag.baseline[0].y)}
              x2={px(tensionFlag.baseline[tensionFlag.baseline.length - 1].x)}
              y2={py(tensionFlag.baseline[tensionFlag.baseline.length - 1].y)}
              stroke="#f97316"
              strokeWidth="2"
            />
            <polygon
              points={`${toPoints(tensionFlag.baseline)} ${toPoints([...tensionFlag.profile].reverse())}`}
              fill="#fed7aa"
              fillOpacity="0.6"
              stroke="#f97316"
              strokeWidth="1.5"
            />
            {tensionFlag.baseline.map(
              (basePoint, index) =>
                index % 2 === 0 &&
                index > 0 && (
                  <line
                    key={`trac-${index}`}
                    x1={px(basePoint.x)}
                    y1={py(basePoint.y)}
                    x2={px(tensionFlag.profile[index].x)}
                    y2={py(tensionFlag.profile[index].y)}
                    stroke="#f97316"
                    strokeWidth="0.8"
                  />
                )
            )}
            <text
              x={px(tensionFlag.profile[tensionFlag.profile.length - 1].x) + 25}
              y={py(tensionFlag.profile[tensionFlag.profile.length - 1].y) + 16}
              fontFamily="sans-serif"
              fontSize="13"
              fill="#f97316"
              fontWeight="bold"
              textAnchor="start"
            >
              {(strains.oppositeExtreme * 1000).toFixed(2)}‰
            </text>
          </>
        )}
        {strainBars.map((bar, index) => (
          <circle key={index} cx={px(bar.x)} cy={py(bar.y)} r={5} fill={bar.strain >= 0 ? '#6366f1' : '#f97316'} />
        ))}
      </SvgImage>
    )
  }

  // Diagramas de Nsd, Msd,x e Msd,y ao longo da altura do pilar, a partir de positionChecks[].
  // A área entre o eixo neutro (z) e a curva é preenchida/hachurada, como no software de referência.
  function EffortsProfileDiagram({ positionChecks, governingPosition }) {
    const sorted = [...positionChecks].sort((a, b) => a.z - b.z)
    const zMin = sorted[0].z
    const zMax = sorted[sorted.length - 1].z
    const zRange = zMax - zMin || 1

    const width = 620
    const height = 340
    const yTop = 40
    const yBottom = 300
    const panelHalfWidth = 55
    const centers = [95, 310, 525]
    const series = [
      { key: 'nSd', title: 'Nsd (kN)' },
      { key: 'mxSdKnm', title: 'Msd,x (kN.m)' },
      { key: 'mySdKnm', title: 'Msd,y (kN.m)' },
    ]
    const fillColor = '#fecaca'
    const strokeColor = '#ef4444'

    const yFor = (z) => yBottom - ((z - zMin) / zRange) * (yBottom - yTop)

    return (
      <SvgImage viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="columnEffortHatch"
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="6" style={{ stroke: strokeColor, strokeWidth: 1, strokeOpacity: 0.5 }} />
          </pattern>
        </defs>
        <line
          x1={20}
          y1={yTop}
          x2={width - 45}
          y2={yTop}
          style={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' }}
        />
        <line
          x1={20}
          y1={yBottom}
          x2={width - 45}
          y2={yBottom}
          style={{ stroke: '#94a3b8', strokeWidth: 1, strokeDasharray: '4 4' }}
        />
        <text x={width - 40} y={yTop + 4} fontSize="13" fontFamily="sans-serif" style={{ fill: '#94a3b8' }}>
          Topo
        </text>
        <text x={width - 40} y={yBottom + 4} fontSize="13" fontFamily="sans-serif" style={{ fill: '#94a3b8' }}>
          Base
        </text>

        {series.map((serie, serieIndex) => {
          const cx = centers[serieIndex]
          const values = sorted.map((position) => Number(position[serie.key]) || 0)
          const maxAbs = Math.max(...values.map((value) => Math.abs(value)), 1e-6)
          const xFor = (value) => cx + (value / maxAbs) * panelHalfWidth

          const curvePoints = sorted.map((position, index) => ({ x: xFor(values[index]), y: yFor(position.z) }))
          const baselinePoints = [...sorted].reverse().map((position) => ({ x: cx, y: yFor(position.z) }))
          const polygonPoints = [...curvePoints, ...baselinePoints].map((point) => `${point.x},${point.y}`).join(' ')

          let extremeIndex = 0
          values.forEach((value, index) => {
            if (Math.abs(value) > Math.abs(values[extremeIndex])) extremeIndex = index
          })
          const extremePosition = sorted[extremeIndex]
          const extremePoint = curvePoints[extremeIndex]
          const isGoverning = extremePosition.key === governingPosition
          const labelAbove = extremePoint.y < (yTop + yBottom) / 2

          return (
            <g key={serie.key}>
              <text
                x={cx}
                y={20}
                fontSize="13"
                fontWeight="bold"
                textAnchor="middle"
                fontFamily="sans-serif"
                style={{ fill: '#374151' }}
              >
                {serie.title}
              </text>
              <line
                x1={cx}
                y1={yTop}
                x2={cx}
                y2={yBottom}
                style={{ stroke: '#9ca3af', strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <polygon
                points={polygonPoints}
                style={{ fill: fillColor, fillOpacity: 0.7, stroke: strokeColor, strokeWidth: 1.5 }}
              />
              <polygon points={polygonPoints} style={{ fill: 'url(#columnEffortHatch)', stroke: 'none' }} />
              <circle
                cx={extremePoint.x}
                cy={extremePoint.y}
                r={isGoverning ? 5 : 4}
                style={{ fill: isGoverning ? '#111827' : strokeColor, stroke: '#ffffff', strokeWidth: 1 }}
              />
              <text
                x={extremePoint.x}
                y={labelAbove ? extremePoint.y - 10 : extremePoint.y + 18}
                fontSize="12"
                fontWeight="bold"
                textAnchor="middle"
                fontFamily="sans-serif"
                style={{ fill: '#374151' }}
              >
                {roundToDecimals(values[extremeIndex], 1)}
              </text>
            </g>
          )
        })}
      </SvgImage>
    )
  }

  function handleAddBar(event) {
    event.preventDefault()

    if (!barDiameter || !barCoverage || !barNx || !barNy) {
      toast.error('Preencha diâmetro, cobrimento, nx e ny')
      return
    }

    const diameterMm = Number(barDiameter)
    const nx = Number(barNx)
    const ny = Number(barNy)
    const d = Number(barCoverage)

    const newBars = []

    // Coordenadas centradas: (0,0) no centro da seção
    // xMin = -b/2, xMax = b/2; yMin = -h/2, yMax = h/2
    const xRange = b / 2 - d
    const yRange = h / 2 - d

    // Calcular posições x
    const xPositions = []
    if (nx === 1) {
      xPositions.push(0)
    } else {
      for (let i = 0; i < nx; i++) {
        const x = -xRange + (i * 2 * xRange) / (nx - 1)
        xPositions.push(roundToDecimals(x, 2))
      }
    }

    // Calcular posições y
    const yPositions = []
    if (ny === 1) {
      yPositions.push(0)
    } else {
      for (let j = 0; j < ny; j++) {
        const y = -yRange + (j * 2 * yRange) / (ny - 1)
        yPositions.push(roundToDecimals(y, 2))
      }
    }

    // Preencher barras em grade completa nx × ny
    for (let j = 0; j < yPositions.length; j++) {
      const y = yPositions[j]
      for (let i = 0; i < xPositions.length; i++) {
        newBars.push({
          x: xPositions[i],
          y,
          diameterMm,
          id: Date.now() + i * 1000 + j,
        })
      }
    }

    setBars(newBars)
    setBarDiameter('')
    setBarCoverage('')
    setBarNx('')
    setBarNy('')
    toast.success(`${newBars.length} barra${newBars.length > 1 ? 's' : ''} adicionada${newBars.length > 1 ? 's' : ''}`)
  }

  function handleRemoveBar(barId) {
    setBars(bars.filter((bar) => bar.id !== barId))
  }

  function handleCopySVG(svgRef) {
    if (!svgRef.current) return

    const svg = svgRef.current
    const canvas = document.createElement('canvas')
    canvas.width = DIAGRAM_SIZE
    canvas.height = DIAGRAM_SIZE

    const ctx = canvas.getContext('2d')
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svg)
    const img = new Image()
    img.onload = () => {
      ctx.drawImage(img, 0, 0)
      canvas.toBlob((blob) => {
        navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]).then(() => {
          toast.success('Diagrama copiado para a área de transferência!')
        })
      })
    }
    img.src = 'data:image/svg+xml;base64,' + btoa(svgString)
  }

  async function handleCalculate(event) {
    event.preventDefault()

    if (!isFormValid || !canCalculate) {
      return
    }

    setIsLoading(true)
    setIsRecalculating(true)

    try {
      const payload = {
        id: columnId || undefined,
        b: Number(b),
        h: Number(h),
        length: Number(length),
        effectiveLengthX: Number(length),
        effectiveLengthY: Number(length),
        concrete: {
          fck: Number(fck),
          gammaC: Number(gammaC),
          alphaCc: Number(alphaCc),
          etaC: Number(etaC),
          epsC2: Number(epsC2),
          epsCu: Number(epsCu),
          n: Number(concreteDiagramN),
        },
        steel: {
          fyk: Number(fyk),
          gammaS: Number(gammaS),
          Es: Number(Es),
        },
        bars: bars.map((bar) => ({
          x: roundToDecimals(Number(b) / 2 + bar.x, 2),
          y: roundToDecimals(Number(h) / 2 + bar.y, 2),
          As: roundToDecimals(barAreaCm2(bar.diameterMm), 4),
        })),
        firstOrder: {
          nSd: Number(nSd),
          mxA: Number(mxA),
          mxB: Number(mxB),
          myA: Number(myA),
          myB: Number(myB),
        },
        options: {
          useMinimumMoment,
          useLocalSecondOrder,
          alwaysCalculateLocalSecondOrder: useLocalSecondOrder && alwaysCalculateLocalSecondOrder,
          localSecondOrderMethod: useLocalSecondOrder ? localSecondOrderMethod : 'none',
          generateMinimumMomentCandidates,
          useConservativeDoubleMinimum,
          limitPinnedEndMoments,
          maxSlendernessForApproximateCurvature: Number(maxSlendernessForApproximateCurvature),
          generalMethodSegments: Number(generalMethodSegments),
          generalMethodMaxIterations: Number(generalMethodMaxIterations),
          generalMethodToleranceDisplacement: Number(generalMethodToleranceDisplacement),
          generalMethodToleranceRelativeDisplacement: Number(generalMethodToleranceRelativeDisplacement),
          generalMethodRelaxationFactor: Number(generalMethodRelaxationFactor),
          creep: {
            mode: 'when-slenderness-over-90',
            phi: 1,
          },
        },
      }

      const response = await api.post('/concrete/column-design', payload)

      updateFromHeaders(response.headers)

      if (response.status === 200) {
        const processedData = processResponseData(response.data)
        setData(processedData)
        setResult(processedData.ok ? 'APROVADO' : 'REPROVADO')

        saveToHistory({
          fck,
          fyk,
          gammaC,
          gammaS,
          alphaCc,
          etaC,
          epsC2,
          epsCu,
          concreteDiagramN,
          Es,
          b,
          h,
          length,
          barDiameter,
          barCoverage,
          barNx,
          barNy,
          bars,
          nSd,
          mxA,
          mxB,
          myA,
          myB,
          useMinimumMoment,
          useLocalSecondOrder,
          alwaysCalculateLocalSecondOrder,
          localSecondOrderMethod,
          generateMinimumMomentCandidates,
          useConservativeDoubleMinimum,
          limitPinnedEndMoments,
          maxSlendernessForApproximateCurvature,
          generalMethodSegments,
          generalMethodMaxIterations,
          generalMethodToleranceDisplacement,
          generalMethodToleranceRelativeDisplacement,
          generalMethodRelaxationFactor,
          columnId,
          result: response.data.ok ? 'APROVADO' : 'REPROVADO',
          data: response.data,
        })

        toast.success('Cálculo realizado com sucesso')
      }
    } catch (error) {
      handleRateLimitError(error)
      setResult('')
      setData({})
    } finally {
      setIsLoading(false)
      setIsRecalculating(false)
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      gammaC,
      gammaS,
      alphaCc,
      etaC,
      epsC2,
      epsCu,
      concreteDiagramN,
      Es,
      b,
      h,
      length,
      barDiameter,
      barCoverage,
      barNx,
      barNy,
      bars,
      nSd,
      mxA,
      mxB,
      myA,
      myB,
      useMinimumMoment,
      useLocalSecondOrder,
      alwaysCalculateLocalSecondOrder,
      localSecondOrderMethod,
      generateMinimumMomentCandidates,
      useConservativeDoubleMinimum,
      limitPinnedEndMoments,
      maxSlendernessForApproximateCurvature,
      generalMethodSegments,
      generalMethodMaxIterations,
      generalMethodToleranceDisplacement,
      generalMethodToleranceRelativeDisplacement,
      generalMethodRelaxationFactor,
      columnId,
      result,
      data,
    }

    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `column-design-${Date.now()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toast.success('Dados salvos')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setGammaC(jsonData.gammaC)
        setGammaS(jsonData.gammaS)
        setAlphaCc(jsonData.alphaCc || 0.85)
        setEtaC(jsonData.etaC || 1)
        setEpsC2(jsonData.epsC2 || 0.002)
        setEpsCu(jsonData.epsCu || 0.0035)
        setConcreteDiagramN(jsonData.concreteDiagramN || 2)
        setEs(jsonData.Es || 210000)
        setB(jsonData.b)
        setH(jsonData.h)
        setLength(jsonData.length)
        setBarDiameter(jsonData.barDiameter)
        setBarCoverage(jsonData.barCoverage)
        setBarNx(jsonData.barNx)
        setBarNy(jsonData.barNy)
        setBars(jsonData.bars || [])
        setNSd(jsonData.nSd)
        setMxA(jsonData.mxA)
        setMxB(jsonData.mxB)
        setMyA(jsonData.myA)
        setMyB(jsonData.myB)
        setUseMinimumMoment(jsonData.useMinimumMoment !== false)
        setUseLocalSecondOrder(jsonData.useLocalSecondOrder !== false)
        setAlwaysCalculateLocalSecondOrder(jsonData.alwaysCalculateLocalSecondOrder !== false)
        setLocalSecondOrderMethod(jsonData.localSecondOrderMethod || 'approximate-curvature')
        setGenerateMinimumMomentCandidates(jsonData.generateMinimumMomentCandidates !== false)
        setUseConservativeDoubleMinimum(jsonData.useConservativeDoubleMinimum || false)
        setLimitPinnedEndMoments(jsonData.limitPinnedEndMoments !== false)
        setMaxSlendernessForApproximateCurvature(jsonData.maxSlendernessForApproximateCurvature || 90)
        setGeneralMethodSegments(jsonData.generalMethodSegments || 10)
        setGeneralMethodMaxIterations(jsonData.generalMethodMaxIterations || 80)
        setGeneralMethodToleranceDisplacement(jsonData.generalMethodToleranceDisplacement || 0.001)
        setGeneralMethodToleranceRelativeDisplacement(jsonData.generalMethodToleranceRelativeDisplacement || 0.0001)
        setGeneralMethodRelaxationFactor(jsonData.generalMethodRelaxationFactor || 1)
        setColumnId(jsonData.columnId)
        setResult(jsonData.result)
        setData(processResponseData(jsonData.data))
        toast.success('Dados carregados')
      } catch (error) {
        toast.error('Erro ao carregar arquivo')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({ result })
  }

  const renderResults = () => {
    if (!result) return null

    return (
      <>
        <Result $isRecalculating={isRecalculating}>
          {isRecalculating && <LoadingOverlay message="Recalculando..." />}
          <h2>Resultados</h2>

          {!data.sectionCheck ? (
            <MathDiv>
              <AlertDiv>
                <p>Não foi possível verificar o pilar com os dados informados.</p>
                <MdError />
              </AlertDiv>
            </MathDiv>
          ) : (
            <>
              <MathDiv>
                <BlockMath
                  math={`N_{d}= ${roundToDecimals(data.finalEfforts?.nSd, 2)} \\, \\text{kN} \\qquad M_{xd}= ${roundToDecimals(data.finalEfforts?.mxSd, 2)} \\, \\text{kN}\\cdot\\text{cm} \\qquad M_{yd}= ${roundToDecimals(data.finalEfforts?.mySd, 2)} \\, \\text{kN}\\cdot\\text{cm}`}
                />
              </MathDiv>
              <MathDiv>
                {data.ok ? (
                  <BlockMath
                    math={`\\dfrac{M_{Sd}}{M_{Rd}}= ${roundToDecimals(data.sectionCheck.ratio, 4)} \\leq 1 \\rightarrow \\text{Pilar aprovado (utilização de } ${roundToDecimals(data.sectionCheck.ratio * 100, 2)}\\%\\text{)}`}
                  />
                ) : (
                  <AlertDiv>
                    <BlockMath
                      math={`\\dfrac{M_{Sd}}{M_{Rd}}= ${roundToDecimals(data.sectionCheck.ratio, 4)} > 1 \\rightarrow \\text{Pilar não aprovado}`}
                    />
                    <MdError />
                  </AlertDiv>
                )}
              </MathDiv>
              {data.sectionCheck.fs && (
                <MathDiv>
                  <BlockMath math={`F.S = ${roundToDecimals(data.sectionCheck.fs, 2)}`} />
                </MathDiv>
              )}
              {data.governingCase && (
                <MathDiv>
                  <BlockMath
                    math={`\\text{Caso governante: } \\text{${data.governingCase}} \\qquad \\text{Posição crítica: } \\text{${positionLabel(data.governingPosition)}}`}
                  />
                </MathDiv>
              )}
            </>
          )}
        </Result>

        {data.sectionCheck && (
          <Result className="results-section" $isRecalculating={isRecalculating}>
            {isRecalculating && <LoadingOverlay />}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <h2>Cálculos</h2>
              <CopyHTMLButton type="button" onClick={handleCopyResults}>
                📋 Copiar Cálculos
              </CopyHTMLButton>
            </div>

            {data.geometry && (
              <>
                <h3>Dados do Pilar</h3>
                <MathDiv>
                  <BlockMath
                    math={`b= ${data.geometry.section?.b} \\, \\text{cm} \\qquad h= ${data.geometry.section?.h} \\, \\text{cm} \\qquad \\ell = ${data.geometry.length} \\, \\text{cm}`}
                  />
                  <BlockMath
                    math={`\\ell_{e,x}= ${data.geometry.effectiveLengthX} \\, \\text{cm} \\qquad \\ell_{e,y}= ${data.geometry.effectiveLengthY} \\, \\text{cm}`}
                  />
                  {data.geometry.grossProperties && (
                    <BlockMath
                      math={`A_c= ${roundToDecimals(data.geometry.grossProperties.Ac, 2)} \\, \\text{cm}^2 \\qquad i_x= ${roundToDecimals(data.geometry.grossProperties.ix, 2)} \\, \\text{cm} \\qquad i_y= ${roundToDecimals(data.geometry.grossProperties.iy, 2)} \\, \\text{cm}`}
                    />
                  )}
                </MathDiv>
              </>
            )}

            {data.firstOrder && (
              <>
                <h3>Esforços de Primeira Ordem</h3>
                <MathDiv>
                  <BlockMath
                    math={`N_{d}= ${roundToDecimals(data.firstOrder.nSd, 2)} \\, \\text{kN} \\qquad M_{xA}= ${roundToDecimals(data.firstOrder.mxA, 2)} \\, \\text{kN}\\cdot\\text{cm} \\qquad M_{xB}= ${roundToDecimals(data.firstOrder.mxB, 2)} \\, \\text{kN}\\cdot\\text{cm}`}
                  />
                  <BlockMath
                    math={`M_{yA}= ${roundToDecimals(data.firstOrder.myA, 2)} \\, \\text{kN}\\cdot\\text{cm} \\qquad M_{yB}= ${roundToDecimals(data.firstOrder.myB, 2)} \\, \\text{kN}\\cdot\\text{cm}`}
                  />
                </MathDiv>
              </>
            )}

            {data.positionChecks && data.positionChecks.length > 0 && (
              <>
                <SectionTitle>Verificação por Posição no Lance</SectionTitle>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Posição</Th>
                        <Th>Cota (cm)</Th>
                        <Th>Nsd (kN)</Th>
                        <Th>Msd,x (kN.m)</Th>
                        <Th>Msd,y (kN.m)</Th>
                        <Th>F.S.</Th>
                        <Th>Utilização (%)</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.positionChecks?.map((position) => (
                        <Tr
                          key={position.key}
                          style={{ fontWeight: position.key === data.governingPosition ? 'bold' : 'normal' }}
                        >
                          <Td>{position.label}</Td>
                          <Td>
                            {position.z !== null && position.z !== undefined ? roundToDecimals(position.z, 1) : '—'}
                          </Td>
                          <Td>
                            {position.nSd !== null && position.nSd !== undefined
                              ? roundToDecimals(position.nSd, 2)
                              : '—'}
                          </Td>
                          <Td>{roundToDecimals(position.mxSdKnm, 2)}</Td>
                          <Td>{roundToDecimals(position.mySdKnm, 2)}</Td>
                          <Td>
                            {position.ratio !== null && position.ratio !== undefined && position.ratio > 1e-6
                              ? roundToDecimals(position.fs, 2)
                              : '—'}
                          </Td>
                          <Td>
                            {position.ratio !== null && position.ratio !== undefined
                              ? roundToDecimals(position.ratio * 100, 1)
                              : '—'}
                          </Td>
                          <Td>{position.ok ? 'Aprovado' : 'Reprovado'}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>

                <SectionTitle>Diagrama de Esforços ao Longo do Pilar</SectionTitle>
                <ImageDiv>
                  <EffortsProfileDiagram
                    positionChecks={data.positionChecks?.filter((p) => p.z !== null) || []}
                    governingPosition={data.governingPosition}
                  />
                </ImageDiv>
              </>
            )}

            {data.minimumMoments && (
              <>
                <h3>Momento Mínimo</h3>
                <MathDiv>
                  <BlockMath
                    math={`M_{x,min}= ${roundToDecimals(data.minimumMoments.mxMin, 2)} \\, \\text{kN}\\cdot\\text{cm} \\qquad M_{y,min}= ${roundToDecimals(data.minimumMoments.myMin, 2)} \\, \\text{kN}\\cdot\\text{cm}`}
                  />
                </MathDiv>
              </>
            )}

            {data.slenderness && (
              <>
                <SectionTitle>Esbeltez</SectionTitle>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Eixo</Th>
                        <Th>λ</Th>
                        <Th>λ₁</Th>
                        <Th>2ª Ordem?</Th>
                      </tr>
                    </thead>
                    <tbody>
                      <Tr>
                        <Td>X</Td>
                        <Td>{roundToDecimals(data.slenderness.lambdaX, 2)}</Td>
                        <Td>{roundToDecimals(data.slenderness.lambda1X, 2)}</Td>
                        <Td>{data.slenderness.needsSecondOrderX ? 'Sim' : 'Não'}</Td>
                      </Tr>
                      <Tr>
                        <Td>Y</Td>
                        <Td>{roundToDecimals(data.slenderness.lambdaY, 2)}</Td>
                        <Td>{roundToDecimals(data.slenderness.lambda1Y, 2)}</Td>
                        <Td>{data.slenderness.needsSecondOrderY ? 'Sim' : 'Não'}</Td>
                      </Tr>
                    </tbody>
                  </Table>
                </TableContainer>
              </>
            )}

            {data.secondOrder?.x && (
              <>
                <SectionTitle>Segunda Ordem</SectionTitle>
                <p style={{ marginBottom: '10px', fontSize: '0.9rem' }}>
                  <strong>Método:</strong> {secondOrderMethodLabel(data.secondOrder.method)}
                </p>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Eixo</Th>
                        <Th>Método</Th>
                        <Th>αb</Th>
                        <Th>κ (× 10⁻⁴ cm⁻¹)</Th>
                        <Th>e₂ (cm)</Th>
                        <Th>M₂ (kN.cm)</Th>
                        <Th>M intermediária (kN.cm)</Th>
                        <Th>EIsec (kN.m²)</Th>
                        <Th>κsec</Th>
                      </tr>
                    </thead>
                    <tbody>
                      <Tr>
                        <Td>X</Td>
                        <Td>{secondOrderMethodLabel(data.secondOrder.x.method)}</Td>
                        <Td>{roundToDecimals(data.secondOrder.x.alphaB, 4)}</Td>
                        <Td>
                          {data.secondOrder.x.curvature !== null
                            ? roundToDecimals(data.secondOrder.x.curvature * 1e4, 4)
                            : '-'}
                        </Td>
                        <Td>
                          {data.secondOrder.x.curvature !== null ? roundToDecimals(data.secondOrder.x.e2, 2) : '-'}
                        </Td>
                        <Td>
                          {data.secondOrder.x.curvature !== null ? roundToDecimals(data.secondOrder.x.M2, 2) : '-'}
                        </Td>
                        <Td>{roundToDecimals(data.secondOrder.x.Mintermediate, 2)}</Td>
                        <Td>
                          {data.secondOrder.x.nmCurvature
                            ? roundToDecimals(data.secondOrder.x.nmCurvature.EIsec / 1e4, 2)
                            : '-'}
                        </Td>
                        <Td>
                          {data.secondOrder.x.nmCurvature
                            ? roundToDecimals(data.secondOrder.x.nmCurvature.kappaSec, 4)
                            : '-'}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>Y</Td>
                        <Td>{secondOrderMethodLabel(data.secondOrder.y.method)}</Td>
                        <Td>{roundToDecimals(data.secondOrder.y.alphaB, 4)}</Td>
                        <Td>
                          {data.secondOrder.y.curvature !== null
                            ? roundToDecimals(data.secondOrder.y.curvature * 1e4, 4)
                            : '-'}
                        </Td>
                        <Td>
                          {data.secondOrder.y.curvature !== null ? roundToDecimals(data.secondOrder.y.e2, 2) : '-'}
                        </Td>
                        <Td>
                          {data.secondOrder.y.curvature !== null ? roundToDecimals(data.secondOrder.y.M2, 2) : '-'}
                        </Td>
                        <Td>{roundToDecimals(data.secondOrder.y.Mintermediate, 2)}</Td>
                        <Td>
                          {data.secondOrder.y.nmCurvature
                            ? roundToDecimals(data.secondOrder.y.nmCurvature.EIsec / 1e4, 2)
                            : '-'}
                        </Td>
                        <Td>
                          {data.secondOrder.y.nmCurvature
                            ? roundToDecimals(data.secondOrder.y.nmCurvature.kappaSec, 4)
                            : '-'}
                        </Td>
                      </Tr>
                    </tbody>
                  </Table>
                </TableContainer>

                {(warningMessages(data.secondOrder.x.warningsDetailed).length > 0 ||
                  warningMessages(data.secondOrder.y.warningsDetailed).length > 0) && (
                  <AlertDiv style={{ marginTop: '12px' }}>
                    <MdError />
                    <div>
                      <strong>Avisos da Segunda Ordem Local:</strong>
                      <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                        {warningMessages(data.secondOrder.x.warningsDetailed).map((warning, idx) => (
                          <li key={`x-${idx}`}>{warning}</li>
                        ))}
                        {warningMessages(data.secondOrder.y.warningsDetailed).map((warning, idx) => (
                          <li key={`y-${idx}`}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  </AlertDiv>
                )}
              </>
            )}

            {data.sectionCheck?.verification?.limitState && (
              <>
                <SectionTitle>Estado Limite da Seção Crítica</SectionTitle>
                <MathDiv>
                  <BlockMath
                    math={`\\theta= ${roundToDecimals(data.sectionCheck.verification.limitState.theta, 4)} \\, \\text{rad} = ${roundToDecimals((data.sectionCheck.verification.limitState.theta * 180) / Math.PI, 2)}°`}
                  />
                  <BlockMath
                    math={`x= ${roundToDecimals(data.sectionCheck.verification.limitState.neutralAxisDepth, 2)} \\, \\text{cm} \\quad \\text{(profundidade da linha neutra)}`}
                  />
                  <BlockMath
                    math={`\\epsilon_{comp}= ${roundToDecimals(data.sectionCheck.verification.limitState.compressedExtremeStrain * 1000, 2)} \\text{ permil} \\qquad \\epsilon_{op}= ${roundToDecimals(data.sectionCheck.verification.limitState.oppositeExtremeStrain * 1000, 2)} \\text{ permil}`}
                  />
                  <BlockMath
                    math={`\\sigma_{c,max}= ${roundToDecimals(data.sectionCheck.verification.limitState.maxConcreteCompressionStress, 2)} \\, \\text{MPa}`}
                  />
                </MathDiv>
              </>
            )}

            {data.sectionCheck?.verification?.diagram && (
              <>
                <SectionTitle>Diagrama de Interação N-Mx-My</SectionTitle>
                <ChartLegend>
                  <span>
                    <span className="swatch line" style={{ background: '#2563eb' }} /> Envoltória resistente (
                    {data.sectionCheck.verification.diagram.force.label} ={' '}
                    {data.sectionCheck.verification.diagram.force.value}{' '}
                    {data.sectionCheck.verification.diagram.force.unit})
                  </span>
                  {data.positionChecks && data.positionChecks.length > 0 ? (
                    data.positionChecks
                      .filter((p) => p.key !== 'minimum-envelope')
                      .map((position) => (
                        <span key={position.key}>
                          <span className="swatch" style={{ background: positionColor(position) }} /> {position.label}
                          {position.key === data.governingPosition ? ' (crítica)' : ''}
                        </span>
                      ))
                  ) : (
                    <span>
                      <span className="swatch" style={{ background: '#16a34a' }} /> Ponto solicitante
                    </span>
                  )}
                  {data.minimumMomentEnvelope?.points && (
                    <span>
                      <span className="swatch" style={{ background: '#dc2626' }} /> Envoltória mínima (colorida por
                      status)
                    </span>
                  )}
                </ChartLegend>
                <ResponsiveContainer width="100%" height={380}>
                  <ComposedChart margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="x"
                      domain={['auto', 'auto']}
                      label={{
                        value: `${data.sectionCheck.verification.diagram.yAxis.label} (${data.sectionCheck.verification.diagram.yAxis.unit})`,
                        position: 'insideBottom',
                        offset: -10,
                      }}
                    />
                    <YAxis
                      type="number"
                      dataKey="y"
                      domain={(() => {
                        // A elipse do minimo e desenhada via <Customized>, entao o Recharts nao a considera
                        // no auto-scale. Estendemos o dominio para incluir a extensao em My da envoltoria.
                        const pts = data.minimumMomentEnvelope?.points ? envelopePontos(data) : []
                        if (pts.length === 0) return ['auto', 'auto']
                        const maxMy = Math.max(...pts.map((p) => Math.abs(p.my))) * 1.05
                        return [(dataMin) => Math.min(dataMin, -maxMy), (dataMax) => Math.max(dataMax, maxMy)]
                      })()}
                      label={{
                        value: `${data.sectionCheck.verification.diagram.xAxis.label} (${data.sectionCheck.verification.diagram.xAxis.unit})`,
                        angle: -90,
                        position: 'insideLeft',
                      }}
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length > 0) {
                          const point = payload[0].payload
                          return (
                            <div
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                padding: '8px 12px',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                                {data.sectionCheck.verification.diagram.yAxis.label}: {roundToDecimals(point.x, 2)}
                              </p>
                              <p style={{ margin: '4px 0', fontWeight: 'bold' }}>
                                {data.sectionCheck.verification.diagram.xAxis.label}: {roundToDecimals(point.y, 2)}
                              </p>
                            </div>
                          )
                        }
                        return null
                      }}
                    />
                    <Line
                      data={(data.sectionCheck.verification.diagram.closedCurve &&
                      data.sectionCheck.verification.diagram.curve.length > 0
                        ? [
                            ...data.sectionCheck.verification.diagram.curve,
                            data.sectionCheck.verification.diagram.curve[0],
                          ]
                        : data.sectionCheck.verification.diagram.curve
                      ).map((pt) => ({ x: pt.y, y: pt.x }))}
                      type="linear"
                      dataKey="y"
                      name="Envoltória resistente"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                    {data.minimumMomentEnvelope?.points && (
                      <Customized component={<MinimumEnvelopePolygon points={envelopePontos(data)} />} />
                    )}
                    {data.positionChecks && data.positionChecks.length > 0 ? (
                      data.positionChecks
                        .filter((p) => p.key !== 'minimum-envelope')
                        .map((position) => (
                          <Line
                            key={position.key}
                            data={[{ x: position.mxSdKnm, y: position.mySdKnm }]}
                            dataKey="y"
                            name={position.label}
                            stroke="none"
                            dot={{
                              r: position.key === data.governingPosition ? 7 : 5,
                              fill: positionColor(position),
                              stroke: position.key === data.governingPosition ? '#111827' : 'none',
                              strokeWidth: position.key === data.governingPosition ? 1.5 : 0,
                            }}
                            isAnimationActive={false}
                            legendType="none"
                          />
                        ))
                    ) : (
                      <Line
                        data={[
                          {
                            x: data.sectionCheck.verification.diagram.demandPoint.y,
                            y: data.sectionCheck.verification.diagram.demandPoint.x,
                          },
                        ]}
                        dataKey="y"
                        name="Ponto solicitante"
                        stroke="none"
                        dot={{ r: 6, fill: '#16a34a' }}
                        isAnimationActive={false}
                        legendType="none"
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </>
            )}

            {data.sectionCheck?.verification?.demandStressDiagram && (
              <>
                <SectionTitle>Diagrama de Tensões da Seção Crítica</SectionTitle>
                <p>
                  A seção é exibida rotacionada para que a linha neutra fique horizontal e o lado comprimido para cima,
                  com o diagrama parábola-retângulo de tensões desenhado a partir do canto mais comprimido.
                </p>
                <ChartLegend>
                  <span>
                    <span className="swatch" style={{ background: '#1d4ed8' }} /> Barra comprimida
                  </span>
                  <span>
                    <span className="swatch" style={{ background: '#f97316' }} /> Barra tracionada
                  </span>
                  <span>
                    <span className="swatch line" style={{ background: '#374151' }} /> Linha neutra
                  </span>
                  <span>
                    <span className="swatch" style={{ background: '#93c5fd' }} /> Diagrama de tensões
                    (parábola-retângulo)
                  </span>
                </ChartLegend>
                <ImageDiv>
                  <StressDiagram stressDiagram={data.sectionCheck.verification.demandStressDiagram} />
                  <CopyImageButton
                    type="button"
                    title="Clique para copiar essa imagem"
                    onClick={() => handleCopySVG(stressDiagramRef)}
                  >
                    <FaRegCopy />
                    Copiar
                  </CopyImageButton>
                </ImageDiv>
                <p>{data.sectionCheck.verification.demandStressDiagram.maxConcreteCompressionStress.label}</p>

                <SectionTitle>Diagrama de Deformações da Seção Crítica</SectionTitle>
                <ChartLegend>
                  <span>
                    <span className="swatch" style={{ background: '#6366f1' }} /> Deformação de compressão
                  </span>
                  <span>
                    <span className="swatch" style={{ background: '#f97316' }} /> Deformação de tração
                  </span>
                  <span>
                    <span className="swatch line" style={{ background: '#374151' }} /> Linha neutra
                  </span>
                </ChartLegend>
                <ImageDiv>
                  <StrainDiagram stressDiagram={data.sectionCheck.verification.demandStressDiagram} />
                </ImageDiv>

                <SectionTitle>Tensões e Deformações nas Barras</SectionTitle>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>x (cm)</Th>
                        <Th>y (cm)</Th>
                        <Th>As (cm²)</Th>
                        <Th>Deformação (‰)</Th>
                        <Th>Tensão (MPa)</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sectionCheck.verification.demandStressDiagram.bars.map((bar, index) => (
                        <Tr key={index}>
                          <Td>{bar.x}</Td>
                          <Td>{bar.y}</Td>
                          <Td>{bar.As}</Td>
                          <Td>{roundToDecimals(bar.strain * 1000, 2)}</Td>
                          <Td>{bar.stress}</Td>
                        </Tr>
                      ))}
                    </tbody>
                  </Table>
                </TableContainer>
              </>
            )}

            {data.allCases && data.allCases.length > 1 && (
              <>
                <SectionTitle>Todos os Casos Analisados</SectionTitle>
                <TableContainer>
                  <Table>
                    <thead>
                      <tr>
                        <Th>Caso</Th>
                        <Th>Posição Crítica</Th>
                        <Th>Mxd (kN.cm)</Th>
                        <Th>Myd (kN.cm)</Th>
                        <Th>Utilização</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.allCases.map((caseItem, idx) => {
                        const caseWarnings = warningMessages(caseItem.warningsDetailed)
                        return (
                          <Tr key={idx} style={{ opacity: caseItem.tag === data.governingCase ? 1 : 0.7 }}>
                            <Td>{caseItem.tag}</Td>
                            <Td>{positionLabel(caseItem.governingPosition)}</Td>
                            <Td>{roundToDecimals(caseItem.finalEfforts.mxSd, 2)}</Td>
                            <Td>{roundToDecimals(caseItem.finalEfforts.mySd, 2)}</Td>
                            <Td>{roundToDecimals(caseItem.sectionCheck.ratio * 100, 2)}%</Td>
                            <Td>
                              {caseItem.sectionCheck.ok ? 'Aprovado' : 'Reprovado'}
                              {caseWarnings.length > 0 && (
                                <MdError
                                  style={{ marginLeft: '6px', color: '#f59e0b', verticalAlign: 'middle' }}
                                  data-tooltip-id="column-design-tooltip"
                                  data-tooltip-content={caseWarnings.join(' | ')}
                                />
                              )}
                            </Td>
                          </Tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </TableContainer>
              </>
            )}

            {warningMessages(data.warningsDetailed, data.warnings).length > 0 && (
              <AlertDiv style={{ marginTop: '20px' }}>
                <MdError />
                <div>
                  <strong>Avisos:</strong>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    {warningMessages(data.warningsDetailed, data.warnings).map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              </AlertDiv>
            )}
          </Result>
        )}
      </>
    )
  }

  return (
    <PageContainer>
      <PageHeader $gradient={CONCRETE_GRADIENT}>
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Pilares</span>
          </div>
          <h1>🏛️ Dimensionamento de Pilares</h1>
          <p className="subtitle">
            Verificação de pilares de concreto armado submetidos à flexo-compressão oblíqua com momento mínimo e efeitos
            de segunda ordem
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🔄 Momento Mínimo</Badge>
            <Badge>📊 Segunda Ordem Local</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta verifica a capacidade resistente de pilares de concreto armado submetidos à força normal e
              flexão oblíqua, aplicando os efeitos do momento mínimo e segunda ordem local conforme NBR 6118:2014.
            </p>
            <p>
              O resultado inclui a análise completa da seção em flexo-compressão oblíqua, mostrando os momentos finais
              de cálculo, índice de utilização e fator de segurança.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <ImageDiv>
            <InputImage />
            <CopyImageButton
              type="button"
              title="Clique para copiar essa imagem"
              onClick={() => handleCopySVG(inputImageRef)}
            >
              <FaRegCopy />
              Copiar
            </CopyImageButton>
          </ImageDiv>

          <form onSubmit={handleCalculate}>
            <h3>Identificação</h3>
            <InputDiv>
              <span>ID do Pilar (opcional)</span>
              <ValidatedInput
                name="columnId"
                type="text"
                value={columnId}
                onChange={(event) => setColumnId(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto - fck (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Resistência Característica do Aço - fyk (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Coeficiente Parcial do Concreto - γc</span>
              <ValidatedInput
                name="gammaC"
                type="number"
                value={gammaC || ''}
                onChange={(event) => setGammaC(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Coeficiente Parcial do Aço - γs</span>
              <ValidatedInput
                name="gammaS"
                type="number"
                value={gammaS || ''}
                onChange={(event) => setGammaS(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <AdvancedOptionsButton
              type="button"
              onClick={() => setShowAdvancedMaterials(!showAdvancedMaterials)}
              $isOpen={showAdvancedMaterials}
            >
              <span className="arrow-icon">{showAdvancedMaterials ? '▼' : '▶'}</span>
              <span className="button-text">{showAdvancedMaterials ? 'Ocultar' : 'Mostrar'} Opções Avançadas</span>
            </AdvancedOptionsButton>

            {showAdvancedMaterials && (
              <>
                <InputDiv>
                  <span>Coeficiente de Longa Duração - αcc</span>
                  <ValidatedInput
                    name="alphaCc"
                    type="number"
                    value={alphaCc || ''}
                    onChange={(event) => setAlphaCc(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>

                <InputDiv>
                  <span>Fator Adicional - ηc</span>
                  <ValidatedInput
                    name="etaC"
                    type="number"
                    value={etaC || ''}
                    onChange={(event) => setEtaC(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>

                <InputDiv>
                  <span>Deformação no Início do Patamar - εc2</span>
                  <ValidatedInput
                    name="epsC2"
                    type="number"
                    value={epsC2 || ''}
                    onChange={(event) => setEpsC2(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>

                <InputDiv>
                  <span>Deformação Última - εcu</span>
                  <ValidatedInput
                    name="epsCu"
                    type="number"
                    value={epsCu || ''}
                    onChange={(event) => setEpsCu(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>

                <InputDiv>
                  <span>Expoente do Diagrama - n</span>
                  <ValidatedInput
                    name="concreteDiagramN"
                    type="number"
                    value={concreteDiagramN || ''}
                    onChange={(event) => setConcreteDiagramN(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>

                <InputDiv>
                  <span>Módulo de Elasticidade do Aço - Es (MPa)</span>
                  <ValidatedInput
                    name="Es"
                    type="number"
                    value={Es || ''}
                    onChange={(event) => setEs(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}

            <h3>Geometria</h3>
            <InputDiv>
              <span>Largura - b (cm)</span>
              <ValidatedInput
                name="b"
                type="number"
                value={b || ''}
                onChange={(event) => setB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Altura - h (cm)</span>
              <ValidatedInput
                name="h"
                type="number"
                value={h || ''}
                onChange={(event) => setH(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Comprimento do Lance (cm)</span>
              <ValidatedInput
                name="length"
                type="number"
                value={length || ''}
                onChange={(event) => setLength(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Armaduras</h3>
            <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <p style={{ marginBottom: '12px', fontSize: '0.9rem', color: '#666' }}>
                Informe as barras por uma grade regular (nx × ny) com diâmetro e cobrimento. Coordenadas centradas (0,0
                no centro da seção).
              </p>
              <InputDiv>
                <span>Diâmetro das barras (mm)</span>
                <input
                  type="number"
                  value={barDiameter}
                  onChange={(event) => setBarDiameter(event.target.value)}
                  disabled={isLoading}
                />
              </InputDiv>
              <InputDiv>
                <span>Margem de cobrimento (d&apos;) — cm</span>
                <input
                  type="number"
                  value={barCoverage}
                  onChange={(event) => setBarCoverage(event.target.value)}
                  disabled={isLoading}
                />
              </InputDiv>
              <InputDiv>
                <span style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', width: '100%' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px' }}>nx</label>
                    <input
                      type="number"
                      value={barNx}
                      onChange={(event) => setBarNx(event.target.value)}
                      disabled={isLoading}
                      placeholder="Quantidade em x"
                      min="1"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '4px' }}>ny</label>
                    <input
                      type="number"
                      value={barNy}
                      onChange={(event) => setBarNy(event.target.value)}
                      disabled={isLoading}
                      placeholder="Quantidade em y"
                      min="1"
                    />
                  </div>
                </span>
              </InputDiv>
              <InputDiv>
                <AddButton
                  type="button"
                  onClick={handleAddBar}
                  disabled={
                    isLoading ||
                    barDiameter === '' ||
                    barCoverage === '' ||
                    barNx === '' ||
                    barNy === '' ||
                    Number(barDiameter) <= 0
                  }
                  title="Adicionar armadura"
                  style={{ width: '100%', padding: '8px', justifyContent: 'center', gap: '8px' }}
                >
                  <FaPlusCircle /> Gerar Grade de Barras
                </AddButton>
              </InputDiv>
            </div>

            {bars.length === 0 ? (
              <ErrorMessage>Adicione ao menos uma barra à seção</ErrorMessage>
            ) : (
              <TableContainer>
                <Table>
                  <thead>
                    <tr>
                      <Th>#</Th>
                      <Th>x (cm)</Th>
                      <Th>y (cm)</Th>
                      <Th>Ø (mm)</Th>
                      <Th>As (cm²)</Th>
                      <Th>Remover</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {bars.map((bar, index) => (
                      <Tr key={bar.id}>
                        <Td>{index + 1}</Td>
                        <Td>{roundToDecimals(bar.x, 2)}</Td>
                        <Td>{roundToDecimals(bar.y, 2)}</Td>
                        <Td>{bar.diameterMm}</Td>
                        <Td>{roundToDecimals(barAreaCm2(bar.diameterMm), 4)}</Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button type="button" onClick={() => handleRemoveBar(bar.id)} disabled={isLoading}>
                            <FaMinusCircle />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}

            <h3>Esforços de Primeira Ordem</h3>
            <p style={{ marginTop: '-8px', marginBottom: '15px', fontSize: '0.85rem', color: '#666' }}>
              Momentos nas duas extremidades do lance (MxA, MxB, MyA, MyB).
            </p>

            <InputDiv>
              <span>Força Normal - Nd (kN)</span>
              <ValidatedInput
                name="nSd"
                type="number"
                value={nSd || ''}
                onChange={(event) => setNSd(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Momento em X no Topo - MxA (kN.cm)</span>
              <ValidatedInput
                name="mxA"
                type="number"
                value={mxA || ''}
                onChange={(event) => setMxA(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Momento em X na Base - MxB (kN.cm)</span>
              <ValidatedInput
                name="mxB"
                type="number"
                value={mxB || ''}
                onChange={(event) => setMxB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Momento em Y no Topo - MyA (kN.cm)</span>
              <ValidatedInput
                name="myA"
                type="number"
                value={myA || ''}
                onChange={(event) => setMyA(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <InputDiv>
              <span>Momento em Y na Base - MyB (kN.cm)</span>
              <ValidatedInput
                name="myB"
                type="number"
                value={myB || ''}
                onChange={(event) => setMyB(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>

            <h3>Opções de Cálculo</h3>
            <CheckboxWrapper>
              <input
                type="checkbox"
                checked={useMinimumMoment}
                onChange={(e) => setUseMinimumMoment(e.target.checked)}
                disabled={isLoading}
              />
              <span>Aplicar Momento Mínimo</span>
            </CheckboxWrapper>

            <CheckboxWrapper>
              <input
                type="checkbox"
                checked={useLocalSecondOrder}
                onChange={(e) => setUseLocalSecondOrder(e.target.checked)}
                disabled={isLoading}
              />
              <span>Considerar Segunda Ordem Local</span>
            </CheckboxWrapper>

            {useLocalSecondOrder && (
              <>
                <CheckboxWrapper>
                  <input
                    type="checkbox"
                    checked={alwaysCalculateLocalSecondOrder}
                    onChange={(e) => setAlwaysCalculateLocalSecondOrder(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span>Sempre Calcular Segunda Ordem Local (mesmo abaixo de λ1)</span>
                </CheckboxWrapper>

                <RadioInputDiv>
                  <span>Método de Segunda Ordem</span>
                  <div>
                    <RadioWrapper>
                      <input
                        type="radio"
                        name="localSecondOrderMethod"
                        checked={localSecondOrderMethod === 'approximate-curvature'}
                        onChange={() => setLocalSecondOrderMethod('approximate-curvature')}
                        disabled={isLoading}
                      />
                      <span>Curvatura</span>
                    </RadioWrapper>
                    <RadioWrapper>
                      <input
                        type="radio"
                        name="localSecondOrderMethod"
                        checked={localSecondOrderMethod === 'NM-curvature'}
                        onChange={() => setLocalSecondOrderMethod('NM-curvature')}
                        disabled={isLoading}
                      />
                      <span>Diagrama N-M</span>
                    </RadioWrapper>
                    <RadioWrapper>
                      <input
                        type="radio"
                        name="localSecondOrderMethod"
                        checked={localSecondOrderMethod === 'general-NM-curvature-uniaxial'}
                        onChange={() => setLocalSecondOrderMethod('general-NM-curvature-uniaxial')}
                        disabled={isLoading}
                      />
                      <span>Método Geral</span>
                    </RadioWrapper>
                  </div>
                </RadioInputDiv>
                <p style={{ marginTop: '-8px', marginBottom: '15px', fontSize: '0.85rem', color: '#666' }}>
                  {localSecondOrderMethod === 'approximate-curvature' && 'Pilar-padrão com curvatura aproximada.'}
                  {localSecondOrderMethod === 'NM-curvature' && 'Pilar-padrão acoplado ao diagrama N-M-1/r.'}
                  {localSecondOrderMethod === 'general-NM-curvature-uniaxial' &&
                    'Método geral desacoplado, com rigidez obtida por direção a partir do diagrama N-M-1/r (ainda não é o método totalmente acoplado N, Mx, My, 1/r).'}
                </p>
              </>
            )}

            <CheckboxWrapper>
              <input
                type="checkbox"
                checked={generateMinimumMomentCandidates}
                onChange={(e) => setGenerateMinimumMomentCandidates(e.target.checked)}
                disabled={isLoading}
              />
              <span>Gerar Candidatos de Momento Mínimo</span>
            </CheckboxWrapper>

            <CheckboxWrapper>
              <input
                type="checkbox"
                checked={useConservativeDoubleMinimum}
                onChange={(e) => setUseConservativeDoubleMinimum(e.target.checked)}
                disabled={isLoading}
              />
              <span>Duplo Mínimo (Conservador)</span>
            </CheckboxWrapper>

            <AdvancedOptionsButton
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              $isOpen={showAdvancedOptions}
            >
              <span className="arrow-icon">{showAdvancedOptions ? '▼' : '▶'}</span>
              <span className="button-text">{showAdvancedOptions ? 'Ocultar' : 'Mostrar'} Opções Avançadas</span>
            </AdvancedOptionsButton>

            {showAdvancedOptions && (
              <CheckboxWrapper>
                <input
                  type="checkbox"
                  checked={limitPinnedEndMoments}
                  onChange={(e) => setLimitPinnedEndMoments(e.target.checked)}
                  disabled={isLoading}
                />
                <span>Limitar Momentos em Extremidades Rotuladas</span>
              </CheckboxWrapper>
            )}

            {showAdvancedOptions && (
              <InputDiv>
                <span>Esbeltez Máxima para Curvatura Aproximada</span>
                <ValidatedInput
                  name="maxSlendernessForApproximateCurvature"
                  type="number"
                  value={maxSlendernessForApproximateCurvature || ''}
                  onChange={(event) => setMaxSlendernessForApproximateCurvature(Number(event.target.value))}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}

            {showAdvancedOptions && localSecondOrderMethod === 'general-NM-curvature-uniaxial' && (
              <>
                <InputDiv>
                  <span>Número de Segmentos (Método Geral)</span>
                  <ValidatedInput
                    name="generalMethodSegments"
                    type="number"
                    value={generalMethodSegments || ''}
                    onChange={(event) => setGeneralMethodSegments(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Máximo de Iterações (Método Geral)</span>
                  <ValidatedInput
                    name="generalMethodMaxIterations"
                    type="number"
                    value={generalMethodMaxIterations || ''}
                    onChange={(event) => setGeneralMethodMaxIterations(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Tolerância de Deslocamento (cm)</span>
                  <ValidatedInput
                    name="generalMethodToleranceDisplacement"
                    type="number"
                    step="0.0001"
                    value={generalMethodToleranceDisplacement || ''}
                    onChange={(event) => setGeneralMethodToleranceDisplacement(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Tolerância Relativa de Deslocamento</span>
                  <ValidatedInput
                    name="generalMethodToleranceRelativeDisplacement"
                    type="number"
                    step="0.00001"
                    value={generalMethodToleranceRelativeDisplacement || ''}
                    onChange={(event) => setGeneralMethodToleranceRelativeDisplacement(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Fator de Relaxação</span>
                  <ValidatedInput
                    name="generalMethodRelaxationFactor"
                    type="number"
                    step="0.05"
                    value={generalMethodRelaxationFactor || ''}
                    onChange={(event) => setGeneralMethodRelaxationFactor(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}

            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input-column').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input
              type="file"
              id="file-input-column"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept=".json"
            />
          </form>
        </Card>

        {renderResults()}
      </Container>

      {isRecalculating && <LoadingOverlay />}
      <ReactTooltip id="column-design-tooltip" place="top" />
    </PageContainer>
  )
}
